import { useEffect, useMemo, useRef, useState } from "react";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "@/lib/api";
import { fmtUSD } from "@/lib/format";

const M = 8; // chart margin (matches LineChart margin below)

export default function PriceChart({
  symbol, assetType, days = 7, height = 260, color,
  markers = [], onMarkerChange,
}) {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const wrapRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get("/market/history", { params: { symbol, asset_type: assetType, days } })
      .then(({ data }) => {
        if (!alive) return;
        setPoints((data.points || []).map(([t, p]) => ({ t, p })));
      })
      .catch(() => alive && setPoints([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [symbol, assetType, days]);

  const [minP, maxP] = useMemo(() => {
    if (points.length < 2) return [0, 1];
    const ps = points.map((x) => x.p);
    return [Math.min(...ps), Math.max(...ps)];
  }, [points]);

  const priceToTop = (price) => M + (1 - (price - minP) / (maxP - minP || 1)) * (height - 2 * M);
  const topToPrice = (top) => maxP - ((top - M) / (height - 2 * M || 1)) * (maxP - minP);

  const startDrag = (id) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = id;
  };
  const moveDrag = (e) => {
    if (!dragRef.current || !wrapRef.current || !onMarkerChange) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const top = Math.min(Math.max(e.clientY - rect.top, M), rect.height - M);
    onMarkerChange(dragRef.current, topToPrice(top));
  };
  const endDrag = () => {
    dragRef.current = null;
  };

  if (loading) {
    return (
      <div style={{ height }} className="w-full flex items-center justify-center text-sm text-gray-400">
        Loading chart…
      </div>
    );
  }
  if (points.length < 2) {
    return (
      <div style={{ height }} className="w-full flex items-center justify-center text-sm text-gray-400">
        No chart data available.
      </div>
    );
  }

  const first = points[0].p;
  const last = points[points.length - 1].p;
  const stroke = color || (last >= first ? "#16a34a" : "#dc2626");
  const visibleMarkers = (markers || []).filter((m) => m.price != null && isFinite(m.price));

  return (
    <div
      ref={wrapRef}
      style={{ height }}
      data-testid="price-chart"
      className="w-full relative select-none"
      onPointerMove={moveDrag}
      onPointerUp={endDrag}
      onPointerLeave={endDrag}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={points} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
          <XAxis dataKey="t" hide type="number" domain={["dataMin", "dataMax"]} />
          <YAxis hide domain={["dataMin", "dataMax"]} />
          <Tooltip
            cursor={{ stroke: "#e5e7eb", strokeWidth: 1 }}
            contentStyle={{
              border: "1px solid #e5e7eb",
              borderRadius: 12,
              padding: "8px 12px",
              fontFamily: "Manrope",
              fontSize: 12,
              boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
            }}
            labelFormatter={(t) => new Date(t).toLocaleString()}
            formatter={(v) => [fmtUSD(v, { dp: v > 10 ? 2 : 4 }), "Price"]}
          />
          <Line type="monotone" dataKey="p" stroke={stroke} strokeWidth={2} dot={false} isAnimationActive={true} />
        </LineChart>
      </ResponsiveContainer>
      {visibleMarkers.map((m) => {
        const clamped = Math.min(Math.max(m.price, minP), maxP);
        return (
          <div
            key={m.id}
            data-testid={`chart-marker-${m.id}`}
            onPointerDown={m.draggable === false ? undefined : startDrag(m.id)}
            className={`absolute left-0 right-0 z-10 ${m.draggable === false ? "" : "cursor-ns-resize"}`}
            style={{ top: priceToTop(clamped) - 8, height: 16, touchAction: "none", pointerEvents: m.draggable === false ? "none" : "auto" }}
          >
            <div className={`absolute left-0 right-0 top-1/2 border-t-2 border-dashed ${m.colorClass || "border-gray-400"}`} />
            <span
              className={`absolute right-2 top-0 text-[9px] font-bold tracking-wide px-1.5 py-0.5 rounded-full ${m.badgeClass || "bg-gray-700 text-white"}`}
            >
              {m.label} {fmtUSD(m.price, { dp: m.price > 10 ? 2 : 4 })}
            </span>
          </div>
        );
      })}
    </div>
  );
}
